import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Clock, ArrowRight, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PageHero, Section } from "@/components/blocks";
import api from "@/lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [active, setActive] = useState("All");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = active === "All" ? {} : { category: active };
    api.get("/blog", { params }).then((r) => { setPosts(r.data.posts); setCategories(r.data.categories); }).catch(() => {}).finally(() => setLoading(false));
  }, [active]);

  const filtered = posts.filter((p) => !q || (p.title + p.excerpt).toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="blog-page">
      <PageHero
        eyebrow="The Teaching Pathways Blog"
        title="Insights for every stage of your teaching career."
        subtitle="Practical, research-informed articles on placements, employability, behaviour management, mentoring and more."
        image="https://images.unsplash.com/photo-1523240795612-9a054b0db644?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
      />
      <Section>
        <div className="flex flex-col lg:flex-row gap-4 justify-between mb-8">
          <div className="flex flex-wrap gap-2">
            <CategoryPill label="All" active={active === "All"} onClick={() => setActive("All")} />
            {categories.map((c) => <CategoryPill key={c} label={c} active={active === c} onClick={() => setActive(c)} />)}
          </div>
          <div className="relative w-full lg:w-64 shrink-0">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input placeholder="Search articles…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="blog-search" />
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((p) => (
              <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}>
                <Card className="overflow-hidden border-slate-200 hover:shadow-xl transition-shadow h-full flex flex-col group">
                  <div className="h-44 overflow-hidden"><img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /></div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100">{p.category}</Badge>
                      <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {p.read_time} min</span>
                    </div>
                    <h3 className="font-display text-lg font-bold text-slate-900 leading-snug group-hover:text-teal-700 transition-colors">{p.title}</h3>
                    <p className="text-sm text-slate-600 mt-2 flex-1 line-clamp-3">{p.excerpt}</p>
                    <div className="mt-4 flex items-center justify-between text-xs text-slate-500">
                      <span>{p.author}</span>
                      <span className="text-teal-600 font-semibold flex items-center gap-1">Read <ArrowRight className="w-3 h-3" /></span>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function CategoryPill({ label, active, onClick }) {
  return (
    <button onClick={onClick} data-testid={`blog-cat-${label}`}
      className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
      {label}
    </button>
  );
}
